import React, {
  useState, useMemo, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import momentTz from 'moment-timezone';

import { getNextNumericalName } from '@avtechsoftware/ra-javascript-utilities/src/utilities/validation/ObjectListUtils';
import { useHistory } from 'react-router-dom';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import {
  createQuietTime,
  editQuietTime,
  selectQuietTimeById,
  selectQuietTimes,
  selectQuietTimesCrudOperation,
  selectQuietTimesCrudStatus,
  setQuietTimeCrudStatus,
} from '../../../../redux/slices/quietTimesSlice/quietTimesSlice';
import useQuietTimes, { defaultTimePeriod } from '../../../hooks/useQuietTimes/useQuietTimes';
import FormButtonGroup from '../../../common/FormButtonGroup/FormButtonGroup';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import QuietTimeForm from './QuietTimeForm/QuietTimeForm';
import {
  pushSessionMessage,
} from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import { CrudType } from '../../../../utility/types/CrudType';

function QuietTimeFormLayout({ quietTimeId }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialQuietTime = useSelector((state) => selectQuietTimeById(state, quietTimeId));
  const quietTimes = useSelector(selectQuietTimes);
  const { timezone } = useSelector(selectSessionInformation);

  const crudStatus = useSelector(selectQuietTimesCrudStatus);
  const crudType = useSelector(selectQuietTimesCrudOperation);

  const newQuietTime = useMemo(() => ({
    name: getNextNumericalName(quietTimes, 'New Quiet Time', 'name'),
    timezone: timezone || momentTz.tz.guess(),
    quietTimeScheduleTimePeriodList: [defaultTimePeriod],
  }), [quietTimes, timezone]);

  const {
    quietTime,
    updateQuietTime,
  } = useQuietTimes({
    initialQuietTime: initialQuietTime || newQuietTime,
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const submitAction = quietTimeId
      ? editQuietTime(quietTime)
      : createQuietTime(quietTime);

    await dispatch(submitAction).unwrap();
  };

  const handleSaveSuccess = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: 'Quiet Time saved successfully.',
    }));

    dispatch(setQuietTimeCrudStatus(CrudStatus.IDLE));

    history.push('/s/actions/quiet-times');
  };

  const handleSaveError = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title: 'Save Error',
      body: 'Error saving Quiet Time.',
    }));

    dispatch(setQuietTimeCrudStatus(CrudStatus.IDLE));
  };

  useEffect(() => {
    if (crudType === CrudType.CREATE || crudType === CrudType.UPDATE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleSaveSuccess();
          break;
        case CrudStatus.ERROR:
          handleSaveError();
          break;
        default:
          break;
      }
    }
  }, [crudType, crudStatus]);

  return (
    <form>
      <div className="row mb-3">
        <div className="col-auto">
          <h2>Quiet Time Schedule Form</h2>
        </div>
      </div>

      <div className="row-col my-1">
        <input
          type="text"
          aria-label="quiet-time template name"
          className="form-control form-control"
          placeholder="New Quiet Time"
          value={quietTime.name}
          onChange={(e) => updateQuietTime('name', e.target.value)}
        />
      </div>

      <hr />

      <QuietTimeForm
        quietTime={quietTime}
        updateQuietTime={updateQuietTime}
      />

      <FormButtonGroup
        onSubmit={handleSubmit}
        redirectLink="/s/actions/quiet-times"
        isSubmitting={isSubmitting}
        submitBtnText="Save Changes"
        loadingBtnText="Saving..."
      />
    </form>
  );
}

QuietTimeFormLayout.defaultProps = {
  quietTimeId: null,
};

QuietTimeFormLayout.propTypes = {
  quietTimeId: PropTypes.number,
};

export default QuietTimeFormLayout;
